export const supportedImageTypes = [
    "image/jpeg",
    "image/png",
    "image/bmp",
];

export type ImageOrientation = 'portrait' | 'landscape' | 'square' | 'unknown';

/** Ask the browser to apply EXIF orientation so width/height match what the user sees. */
export const visualImageBitmapOptions: ImageBitmapOptions = {
    imageOrientation: 'from-image',
};

export const isImageSupported = (file: File): boolean => {
    return supportedImageTypes.includes(file.type);
};

/**
 * Classifies orientation using the same width vs height rule as pdf-gen getImage.
 * Landscape is width > height; square and taller images are portrait-compatible.
 */
export const classifyImageOrientation = (width: number, height: number): ImageOrientation => {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return 'unknown';
    }
    if (width > height) {
        return 'landscape';
    }
    if (width === height) {
        return 'square';
    }
    return 'portrait';
};

const closeImageBitmap = (bitmap: ImageBitmap): void => {
    if (typeof bitmap.close === 'function') {
        bitmap.close();
    }
};

const readDimensionsFromImageElement = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        const cleanup = (): void => {
            URL.revokeObjectURL(objectUrl);
            image.onload = null;
            image.onerror = null;
            image.src = '';
        };
        image.onload = () => {
            const width = image.naturalWidth;
            const height = image.naturalHeight;
            cleanup();
            resolve({ width, height });
        };
        image.onerror = () => {
            cleanup();
            reject(new Error('Unable to decode image'));
        };
        image.src = objectUrl;
    });
};

/**
 * Prefer createImageBitmap with EXIF applied. If that options object is rejected,
 * fall back to HTMLImageElement (typically display-oriented), not to unoriented pixels.
 */
const readImageDimensions = async (file: File): Promise<{ width: number; height: number }> => {
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(file, visualImageBitmapOptions);
            const dimensions = { width: bitmap.width, height: bitmap.height };
            closeImageBitmap(bitmap);
            return dimensions;
        } catch {
            return readDimensionsFromImageElement(file);
        }
    }
    return readDimensionsFromImageElement(file);
};

export const getImageOrientation = async (file: File): Promise<ImageOrientation> => {
    try {
        const { width, height } = await readImageDimensions(file);
        return classifyImageOrientation(width, height);
    } catch {
        return 'unknown';
    }
};

/**
 * Returns true unless the image is a confirmed landscape.
 * Unknown decode results are accepted so valid files are not rejected by a fragile check.
 */
export const isPortraitCompatibleImage = async (file: File): Promise<boolean> => {
    const orientation = await getImageOrientation(file);
    return orientation !== 'landscape';
};

export type InspectionImageValidationReason = 'unsupported_type' | 'landscape';

export type InspectionImageValidationResult =
    | { accepted: true }
    | { accepted: false; reason: InspectionImageValidationReason };

/**
 * MIME check always runs. Portrait orientation is checked only when opted in.
 * Decode failures do not reject a supported file.
 */
export const validateInspectionImageFile = async (
    file: File,
    options: { requirePortraitOrientation?: boolean } = {}
): Promise<InspectionImageValidationResult> => {
    if (!isImageSupported(file)) {
        return { accepted: false, reason: 'unsupported_type' };
    }
    if (options.requirePortraitOrientation) {
        const isPortraitCompatible = await isPortraitCompatibleImage(file);
        if (!isPortraitCompatible) {
            return { accepted: false, reason: 'landscape' };
        }
    }
    return { accepted: true };
};
